import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import {
  FoodControlPointInstance,
  FoodManagementControls,
  FoodReport,
  getFoodControlDetail,
  InstanceStatus,
  useControlPointTypeTranslations,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { InfoTextByType } from "./InfoTextByType";
import { OverdueControlTexts } from "./TextTypes/OverdueControlTexts";

interface InfoModalProps extends ModalProps {
  selectedReport?: {
    report: FoodReport;
    instances: FoodControlPointInstance[];
    date: Date;
  };
  onAddMoreClick?: () => void;
}

export function InfoModal({
  selectedReport,
  onAddMoreClick,
  ...props
}: InfoModalProps) {
  const { i18n, t } = useTranslation();
  const { isSuperAdmin } = useCurrentUserGroups();

  const fetchAllData = async () => {
    if (!selectedReport) {
      return;
    }

    const promises = selectedReport.instances
      .filter((i) => i.status !== InstanceStatus.overdueSubmitted)
      .map(async (instance) => ({
        instance,
        isNewArchitecture: !!instance.data,
        controlData:
          instance.id && !instance.data
            ? await getFoodControlDetail(
                selectedReport.report.type as FoodManagementControls,
                instance.id,
              )
            : instance.data
              ? JSON.parse(instance.data)
              : undefined,
      }));
    return Promise.all(promises);
  };

  const controlPointTypeTranslations = useControlPointTypeTranslations();

  const { data: controls } = useQuery({
    queryKey: [
      "foodControlDetails",
      selectedReport?.report.type,
      selectedReport?.instances,
    ],
    queryFn: fetchAllData,
    enabled: selectedReport && selectedReport?.instances.length > 0,
  });

  return (
    <Modal
      {...props}
      title={` ${selectedReport && controlPointTypeTranslations[selectedReport?.report.type]} / ${
        selectedReport
          ? Intl.DateTimeFormat(i18n.language, {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })
              .format(selectedReport?.date)
              .replace(/(\d{2} \w{3}) (\d{4})/, "$1, $2")
          : ""
      }`}
      headerButton={
        isSuperAdmin && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onAddMoreClick?.()}
          >
            {t("common.Add More")}
          </Button>
        )
      }
    >
      <OverdueControlTexts
        datas={
          selectedReport?.instances.filter(
            (i) => i.status === InstanceStatus.overdueSubmitted,
          ) || []
        }
        callBack={props.onClose}
      />
      <InfoTextByType
        controls={controls?.filter((c) => c.controlData)}
        type={selectedReport?.report.type}
        callBack={props.onClose}
      />
      {process.env.NODE_ENV === "development" && (
        <ul>
          Incoming Data
          {controls?.map(({ controlData, instance }) => (
            <li key={instance.id}>
              <div
                style={{
                  border: "1px solid #ccc",
                  marginBottom: "10px",
                  padding: "10px",
                }}
              >
                {controlData &&
                  Object.entries(controlData)?.map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong>{" "}
                      {typeof value === "object" && value !== null
                        ? JSON.stringify(value)
                        : String(value)}
                    </div>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
